import { useAuth } from "@auth/AuthContext"
import { AlertTriangle } from "lucide-react"
import { useTranslation } from "react-i18next"

/**
 * PRD-22 — persistent banner shown at the top of every authenticated
 * page when the logged-in professor's emails are blocked: an internal
 * account (`@platonica.io` / `@sumus.mx`, FR-16/17), or — FR-19 — any account whose
 * emails Platonica staff paused from the admin console. The second case is
 * a real teacher, so the copy says what happened instead of "cuenta
 * interna", which would be false for them.
 *
 * Backend-driven: reads `professor.emails_blocked` from the auth
 * context (the SPA never re-implements the policy — see
 * `core.email_policy.is_email_blocked_for`).
 *
 * Returns null when the guard isn't firing so the dominant 99% case
 * (real teacher accounts) pays no DOM cost.
 *
 * Not dismissible — staff need to keep seeing this until they log
 * out. Dismissal would defeat the "I forgot I was on an internal
 * account" failure mode the guard exists to prevent.
 */
export function InternalAccountBanner() {
  const { t } = useTranslation("common")
  const { professor } = useAuth()

  if (!professor?.emails_blocked) return null
  const paused = professor.email_block_reason === "ops_paused"

  return (
    <div
      role="status"
      aria-live="polite"
      className="border-attention bg-attention-soft text-attention-ink flex items-start gap-2 border-b px-4 py-2 text-sm sm:items-center"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 sm:mt-0" aria-hidden="true" />
      <div className="flex flex-col gap-0 sm:flex-row sm:items-center sm:gap-1.5">
        <span className="font-semibold">
          {paused ? t("paused_emails_banner.title") : t("internal_account_banner.title")}
        </span>
        <span className="text-attention-ink">
          {paused ? t("paused_emails_banner.body") : t("internal_account_banner.body")}
        </span>
      </div>
    </div>
  )
}
